import {EditorView} from '@codemirror/view'
import type {FileType} from '@github-ui/use-unified-file-select'
import {
  BoldIcon,
  CodeIcon,
  CodeSquareIcon,
  HeadingIcon,
  ItalicIcon,
  LinkIcon,
  ListOrderedIcon,
  ListUnorderedIcon,
  QuoteIcon,
  StrikethroughIcon,
} from '@primer/octicons-react'
import {ActionBar} from '@primer/react'

import {formatCodeBlock} from '../codemirror/commands/code-blocks/format-code-block'
import {isLabeledLink} from '../codemirror/commands/inline-formatting/format-link'
import {
  formatBlockquoteKeybinding,
  formatBoldKeybinding,
  formatHeading3Keybinding,
  formatInlineCodeKeybinding,
  formatItalicKeybinding,
  formatLinkKeybinding,
  formatOrderedListKeybinding,
  formatStrikethroughKeybinding,
  formatUnorderedListKeybinding,
} from '../codemirror/extensions/markdown-keymap'
import {FileUploadToolbarButton} from './FileUploadToolbarButton'
import styles from './Toolbar.module.css'
import {ToolbarButton} from './ToolbarButton'

interface ToolbarProps {
  view: EditorView | null
  acceptedFileTypes?: FileType[]
  handleFileUpload?: (file: File) => Promise<void>
}

export const Toolbar = ({view, acceptedFileTypes, handleFileUpload}: ToolbarProps) => {
  return (
    <ActionBar aria-label="Formatting tools" className={styles.actionBar}>
      <ToolbarButton
        view={view}
        aria-label="Bold"
        icon={BoldIcon}
        command={formatBoldKeybinding}
        matchingNodeFilter={n => n.type === 'strong'}
      />
      <ToolbarButton
        view={view}
        aria-label="Italic"
        icon={ItalicIcon}
        command={formatItalicKeybinding}
        matchingNodeFilter={n => n.type === 'emphasis'}
      />
      <ToolbarButton
        view={view}
        aria-label="Link"
        icon={LinkIcon}
        command={formatLinkKeybinding}
        matchingNodeFilter={n => n.type === 'link' && isLabeledLink(n)}
      />
      <ToolbarButton
        view={view}
        aria-label="Code"
        icon={CodeIcon}
        command={formatInlineCodeKeybinding}
        matchingNodeFilter={n => n.type === 'inlineCode'}
      />
      <ToolbarButton
        view={view}
        aria-label="Quote"
        icon={QuoteIcon}
        command={formatBlockquoteKeybinding}
        matchingNodeFilter={n => n.type === 'blockquote'}
      />
      <ActionBar.Divider />
      <ToolbarButton
        view={view}
        aria-label="Unordered list"
        icon={ListUnorderedIcon}
        command={formatUnorderedListKeybinding}
        matchingNodeFilter={n => n.type === 'list' && !n.ordered}
      />
      <ToolbarButton
        view={view}
        aria-label="Ordered list"
        icon={ListOrderedIcon}
        command={formatOrderedListKeybinding}
        matchingNodeFilter={n => n.type === 'list' && !!n.ordered}
      />
      {/* <ToolbarButton onClick={() => {}} icon={TasklistIcon} aria-label="Task list" /> */}
      <ActionBar.Divider />
      {handleFileUpload && (
        <>
          <FileUploadToolbarButton acceptedFileTypes={acceptedFileTypes} onFileSelect={handleFileUpload} />
        </>
      )}
      {/* <ToolbarButton onClick={() => {}} icon={ReplyIcon} aria-label="Saved replies" /> */}
      {/* <ToolbarButton onClick={() => {}} icon={DiffIgnoredIcon} aria-label="Slash commands" /> */}
      {/* <ToolbarButton onClick={() => {}} icon={AlertIcon} aria-label="Alert" /> */}
      <ToolbarButton
        view={view}
        aria-label="Code block"
        icon={CodeSquareIcon}
        command={formatCodeBlock}
        matchingNodeFilter={n => n.type === 'code'}
      />
      {/* <ToolbarButton onClick={() => {}} icon={TableIcon} aria-label="Table" /> */}
      {/* <ToolbarButton onClick={() => {}} icon={MultiSelectIcon} aria-label="Details" /> */}
      {/* <ToolbarButton onClick={() => {}} icon={SquareIcon} aria-label="Footnote" /> */}
      <ToolbarButton
        view={view}
        aria-label="Heading 3"
        icon={HeadingIcon}
        command={formatHeading3Keybinding}
        matchingNodeFilter={n => n.type === 'heading' && n.depth === 3}
      />
      {/* <ToolbarButton onClick={() => {}} icon={HorizontalRuleIcon} aria-label="Horizontal Rule" /> */}
      {/* <ToolbarButton onClick={() => {}} icon={NumberIcon} aria-label="Math block" /> */}
      {/* <ToolbarButton onClick={() => {}} icon={CrossReferenceIcon} aria-label="Reference" /> */}
      <ToolbarButton
        view={view}
        aria-label="Strikethrough"
        icon={StrikethroughIcon}
        command={formatStrikethroughKeybinding}
        matchingNodeFilter={n => n.type === 'delete'}
      />
    </ActionBar>
  )
}
Toolbar.displayName = 'HybridMarkdownEditor.Toolbar'
